/**
 * Runtime capability probe: does the LIVE backend persist the full theme recipe?
 *
 * Theming v2 wants to round-trip the recipe (treatment · seed · polarity · font)
 * through a `treatment` text field on `ThemeContentInput` (WS3.1 / #4827). Until
 * that lands the portal must NOT send the field: a pre-#4827 server rejects
 * unknown input-object fields, which would fail the WHOLE save/publish. The
 * compile-time gql.tada snapshot can't tell us whether the deployed schema has
 * it (it's whatever was generated locally), so we ask the live schema directly.
 *
 * This is the "real capability check" #4824 asked for — schema introspection for
 * the field, so save/publish carries the recipe the moment #4827 is deployed and
 * silently falls back to 6-hex-only until then. Any failure ⇒ false: the probe
 * must never be the reason a save/publish breaks (graceful-degrade).
 *
 * Cached for the session — the schema doesn't change under a running portal.
 */
import { gqlRawRequest } from "@/lib/graphql";

const INTROSPECT_THEME_CONTENT_INPUT = `
  query ThemeTreatmentCapability {
    __type(name: "ThemeContentInput") {
      inputFields {
        name
      }
    }
  }
`;

interface CapabilityIntrospection {
  __type: { inputFields: Array<{ name: string }> | null } | null;
}

let cached: Promise<boolean> | null = null;

/**
 * Whether `ThemeContentInput.treatment` exists on the deployed schema. Resolved
 * once and memoised; safe to call per write.
 */
export function themeTreatmentColumnSupported(): Promise<boolean> {
  if (!cached) cached = probe();
  return cached;
}

async function probe(): Promise<boolean> {
  try {
    const data = await gqlRawRequest<CapabilityIntrospection>(INTROSPECT_THEME_CONTENT_INPUT);
    return data.__type?.inputFields?.some((f) => f.name === "treatment") ?? false;
  } catch {
    // Endpoint unreachable, not signed in, introspection disabled, … — treat the
    // recipe field as unavailable and keep publishing the generated 6-hex.
    return false;
  }
}

/** Test seam: forget the memoised probe result so the next call re-introspects. */
export function resetThemeTreatmentCapabilityCache(): void {
  cached = null;
}
